import loadArcGIS from './utils/loadArcGIS';
import { addLegendStylesAfterRender, customViewMapStyles } from './utils/customViewMapStyles';
import { search } from './mapSearch';
import { buildCards, updateVisibleCards } from '../ProjectCard';
import { resetLayerSelection } from './utils/projectLayers';

/**
 * Singleton promise for loading ALL ESRI AMD modules via a single window.require call.
 * This prevents multiple init.js requests from the ArcGIS AMD loader – each module
 * pulled in via a separate require() triggers its own chunk load.
 * By bundling them into one call, the loader resolves dependencies in a single pass.
 */
let esriModulesPromise = null;

function loadEsriModules() {
  if (esriModulesPromise) {
    return esriModulesPromise;
  }

  esriModulesPromise = new Promise((resolve, reject) => {
    if (!window.require) {
      reject(new Error('ArcGIS API wurde nicht geladen.'));
      return;
    }

    // Load ALL modules needed across the entire InteractiveMap feature in ONE call.
    // This includes modules previously loaded on-demand in searchUtils.js
    // (esri/Graphic, esri/geometry/Circle), which caused additional init.js fetches.
    window.require(
      [
        'esri/WebMap',
        'esri/views/MapView',
        'esri/widgets/Home',
        'esri/geometry/Extent',
        'esri/widgets/Legend',
        'esri/widgets/Expand',
        'esri/Graphic',
        'esri/geometry/Circle',
      ],
      (WebMap, MapView, Home, Extent, Legend, Expand, Graphic, Circle) => {
        resolve({ WebMap, MapView, Home, Extent, Legend, Expand, Graphic, Circle });
      },
      (error) => {
        esriModulesPromise = null;
        reject(error);
      },
    );
  });

  return esriModulesPromise;
}

/**
 * The main class that bind all together
 */
class InteractiveMap {
  constructor(options = {}) {
    this.containerId = 'viewDiv';
    this.apiKey = options.element.dataset.apiKey;
    this.options = Object.assign(
      options,
    );
    this.searchParent = document.getElementById('js-project-search-from');
    this.hasSearchTermResults = false;
  }

  async init() {
    try {
      await loadArcGIS();
      await this.initMap();
    } catch (loadError) {
      console.error('Fehler beim Laden der ArcGIS API:', loadError);
    }
  }

  /**
   * Init map by import modules and executes createMap method
   *
   * @returns {Promise<void>}
   */
  async initMap() {
    const modules = await loadEsriModules();

    this.WebMap = modules.WebMap;
    this.MapView = modules.MapView;
    this.Home = modules.Home;
    this.Extent = modules.Extent;
    this.Legend = modules.Legend;
    this.Expand = modules.Expand;

    // Store Graphic + Circle so they can be passed to search utilities
    // instead of a second window.require() call at search time
    this.Graphic = modules.Graphic;
    this.Circle = modules.Circle;

    await this.createMap();
  }

  /**
   * Build the view map and parts of widgets (Home, zoom and legend)
   *
   * @returns {Promise<void>}
   */
  async createMap() {
    // Single combined debounce timer for all extent/zoom settle logic
    let settleTimer = null;

    // Flag: protect the initial load phase from zoom/extent watchers
    // that would otherwise hide layers and re-trigger all feature requests
    let initialLoadComplete = false;

    // Flag: true while a programmatic scale correction is in progress,
    // prevents watchers from reacting to our own corrections
    let isCorrectingScale = false;

    // Set the initial (reset) extent of the view (middle of germany)
    const germanyExtent = new this.Extent({
      xmin: 652000,
      ymin: 5980000,
      xmax: 1670000,
      ymax: 7360000,
      spatialReference: { wkid: 3857 },
    });

    // Set the reference api key to amprion arcgis map data
    const webMap = new this.WebMap({
      portalItem: {
        id: this.apiKey,
      },
    });

    // Create the view with given options and restrictions
    const view = new this.MapView({
      container: this.containerId,
      map: webMap,
      center: [7.9, 51],
      zoom: 5,
      constraints: {
        geometry: germanyExtent,
        minZoom: 5,
        maxZoom: 16,
        snapToZoom: true,
        minScale: 9000,
        rotationEnable: false,
      },
      interactionOptions: {
        zoom: {
          duration: 0,
          inertia: false,
        },
        drag: {
          inertia: false,
        },
      },
      ...this.options,
    });

    // prevent hit-test on events
    view.popup.autoOpenEnabled = false;
    const block = evt => evt.stopPropagation();
    view.on('pointer-move', block);
    view.on('click', block);
    view.on('double-click', block);
    view.on('pointer-down', block);
    view.on('pointer-up', block);

    // Home button widget in view
    const home = new this.Home({
      view,
      viewpoint: {
        center: [7.9, 51],
        zoom: 5,
        spatialReference: { wkid: 3857 },
      },
    });

    view.ui.add(home, 'top-right');
    home.id = 'home';

    // Reset card list on home-button click when home is ready
    home.when(() => {
      const btn = home.container.querySelector('calcite-button');
      btn.addEventListener('click', () => {
        /* eslint-disable no-restricted-globals */
        setTimeout(() => {
          const cards = document.querySelectorAll('.js-mol-project-card');
          cards.forEach((card) => {
            card.classList.add('is-visible');
            card.classList.remove('is-active');
          });
          resetLayerSelection(webMap);
        }, 300);
      });
    });

    // Move the zoom-widget to the right
    view.ui.move('zoom', 'top-right');

    /**
     * On/when view is loaded
     */
    view.when(async () => {
      // Wait for DOM to init search
      /* eslint-disable no-restricted-globals */
      requestAnimationFrame(() => {
        // Pass pre-loaded Graphic & Circle modules to avoid a second require() call
        search(view, webMap, germanyExtent, { Graphic: this.Graphic, Circle: this.Circle });
      });

      // Initialize project cards, add custom styles for widgets adjust legend widget
      await this.initProjectCards(webMap, view);
      customViewMapStyles(view);

      // Build custom legend widget
      const legend = new this.Legend({ view });
      const legendExpand = new this.Expand({
        view,
        content: legend,
        expandTooltip: 'Legende',
        expandIcon: 'legend',
        collapseIcon: 'chevrons-right',
        mode: 'floating',
      });

      view.ui.add(legendExpand, 'top-right');
      legendExpand.id = 'legend';

      // Add custom legend container style (custom height, width and background color)
      addLegendStylesAfterRender(view);

      // Mark initial load as complete – watchers can now do their work
      // We delay this slightly to let all initial layer tiles settle
      // without being interrupted by zoom/extent watchers

      /* eslint-disable no-restricted-globals */
      setTimeout(() => {
        initialLoadComplete = true;

        if (view.map && view.map.layers.items) {
          const mapWrapper = document.querySelector('#js-org-interactive-map__map-wrapper');
          mapWrapper.classList.remove('loading');
        }
      }, 1000);
    });

    /**
     * Fetch throttling – only applied to ESRI URLs, and only after initial load.
     * During initial load, requests flow freely so layers appear as fast as possible.
     */
    const originalFetch = window.fetch;
    let throttleRequests = false;
    window.fetch = (resource, fetchOptions) => {
      // Only throttle ESRI URLs, never other traffic
      const url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
      const isArcGIS = url.indexOf('arcgis.com') !== -1 || url.indexOf('arcgisonline.com') !== -1;

      if (throttleRequests && isArcGIS) {
        return new Promise((resolve) => {
          /* eslint-disable */
          setTimeout(() => {
            resolve(originalFetch(resource, fetchOptions));
          }, 350);
          /* eslint-enable */
        });
      }

      return originalFetch(resource, fetchOptions);
    };

    /**
     * Watchers on events
     */

    // Register term-changed listener ONCE (not inside extent watcher)
    this.searchParent.addEventListener('term-changed', (event) => {
      this.hasSearchTermResults = event.detail;

      if (!this.hasSearchTermResults) {
        resetLayerSelection(webMap);
      }
    });

    let lastExtentJson = '';
    let lastZoom = view.zoom;

    /**
     * Unified settle handler – called once after zoom/pan/extent
     * changes have stopped for a given duration. Replaces the
     * separate debounce timers that used to fire independently
     * and cascade into each other.
     */
    const onViewSettled = () => {
      // Re-enable throttled requests
      throttleRequests = false;

      // Restore hidden layers
      view.map.layers.forEach((layer) => {
        /* eslint-disable no-param-reassign */
        if (!layer.visible) {
          layer.visible = true;
        }
        /* eslint-enable no-param-reassign */
      });

      // Update visible cards based on current extent
      const extentJson = JSON.stringify(view.extent);
      if (extentJson !== lastExtentJson) {
        lastExtentJson = extentJson;

        if (!this.hasSearchTermResults) {
          this.getVisibleBookmarks(view).then((results) => {
            updateVisibleCards(results);
          });
        }
      }
    };

    /**
     * Schedule a settle – resets the timer on every call so
     * rapid successive changes (zoom steps, panning) only
     * trigger one settle at the end.
     *
     * @param {number} delay - ms to wait after last change
     */
    const scheduleSettle = (delay = 400) => {
      /* eslint-disable no-restricted-globals */
      clearTimeout(settleTimer);
      settleTimer = setTimeout(onViewSettled, delay);
      /* eslint-enable no-restricted-globals */
    };

    // Replaces the old dual-timer approach (extentDebounceTimer + stationaryTimeout).
    // Now just schedules a single settle.
    view.watch('extent', () => {
      if (!initialLoadComplete || isCorrectingScale) {
        return;
      }

      scheduleSettle(400);
    });

    // Only reacts to actual zoom CHANGES after initial load.
    // Hides layers and throttles fetch until settled.
    view.watch('zoom', (zoom) => {
      if (!initialLoadComplete || isCorrectingScale) {
        lastZoom = zoom;
        return;
      }

      if (zoom !== lastZoom) {
        lastZoom = zoom;

        // hide layers on zoom to prevent too many requests to ESRI api
        view.map.layers.forEach((layer) => {
          /* eslint-disable no-param-reassign */
          layer.visible = false;
        });

        // disable requests to prevent too many actions to api
        throttleRequests = true;

        // The extent watcher will also fire and schedule a settle,
        // but we schedule here too in case extent doesn't change
        // (e.g. snapping back to same zoom level)
        scheduleSettle(400);
      }
    });

    // Check rotation
    view.watch('rotation', (value) => {
      // Prevent rotation in view map
      if (value !== 0) {
        view.rotation = 0;
      }
    });

    // Check Scale for changes – guarded to prevent re-trigger loop
    view.watch('scale', (scale) => {
      // break if view scale is under 9000 -> set to min 9000
      if (scale < 9000 && !isCorrectingScale) {
        isCorrectingScale = true;
        view.scale = 9000;

        // Release guard after the correction has settled
        /* eslint-disable no-restricted-globals */
        setTimeout(() => {
          isCorrectingScale = false;
        }, 300);
        /* eslint-enable no-restricted-globals */
      }
    });
  }

  /**
   * initialize project cards fill in
   *
   * @param map
   * @param view
   * @returns {Promise<void>}
   */
  async initProjectCards(map, view) {
    if (map && view) {
      await buildCards(map, view);
    }
  }

  /**
   * Create Update on project cards depending on given bookmarks in the current EXTENT
   *
   * @param view
   * @returns {Promise<{id: string, name: string, visible: boolean}[]>}
   */
  async getVisibleBookmarks(view) {
    const extent = view.extent;
    const currentExtent = this.shrinkExtent(extent);

    const bookmarks = (view.map.bookmarks && view.map.bookmarks.items) ?
      view.map.bookmarks.items : [];

    // find visible bookmarks
    const visibleBookmarks = bookmarks.filter((bm) => {
      if (!bm.viewpoint || !bm.viewpoint.targetGeometry) {
        return false;
      }

      const geom = bm.viewpoint.targetGeometry;
      let bmExtent = null;

      if (geom && geom.extent) {
        bmExtent = geom.extent || geom;
      }

      if (!bmExtent) {
        return false;
      }

      // the intersect checks raw, if another extent, cross this geo extent from view
      return this.fastIntersects(bmExtent, currentExtent);
    });

    // collect all layers
    const allLayers = Array.from(
      view.map.layers.items.map(layer => ({
        id: layer.id,
        title: layer.title,
      })),
    );

    // results into bookmarks with same name like group layer id
    return allLayers.map((layer) => {
      const match = !!visibleBookmarks.find(bm => bm.name === layer.id);

      return {
        id: layer.id,
        name: layer.title,
        visible: match,
      };
    });
  }

  /**
   * Minimize the extent, that matter - needs to be smaller than 100%
   *
   * @param extent - given extent
   * @param factor - extent factor 0 to 1 (1 = 100%)
   * @returns {*} - Object from Extent
   */
  shrinkExtent(extent, factor = 0.8) {
    const dx = (extent.width * (1 - factor)) / 2;
    const dy = (extent.height * (1 - factor)) / 2;

    /* eslint-disable */
    return new this.Extent({
      xmin: extent.xmin + dx,
      xmax: extent.xmax - dx,
      ymin: extent.ymin + dy,
      ymax: extent.ymax - dy,
      spatialReference: extent.spatialReference,
    });
    /* eslint-enable */
  }

  /**
   * This function simply checks if extents (view-extent vs. bookmark-extent) have intersections
   *
   * @param a - extent one
   * @param b - extent two
   * @returns {boolean} - returns if intersects or not
   */
  fastIntersects(a, b) {
    return !(
      a.xmax < b.xmin ||
      a.xmin > b.xmax ||
      a.ymax < b.ymin ||
      a.ymin > b.ymax
    );
  }
}

export default InteractiveMap;
